package gr.tech.site.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.components.layouts.PageLayoutData
import gr.tech.site.components.states.FaqAccordion
import gr.tech.site.components.states.FaqItem
import gr.tech.site.styles.FaqLightAccordionStyle
import gr.tech.site.styles.Theme
import gr.tech.site.styles.landings.*
import gr.tech.site.styles.wideCappedWidth
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

@InitRoute
fun initFaqPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(
        showContactHeader = true,
        title = "FAQ — Energy Rebates for Cannabis Cultivators & Indoor Growers",
        description = "Answers to the most common questions about energy rebates, DLC-qualified grow lights, HVAC incentives, and the rebate process for cannabis and cultivation facilities.",
        canonicalUrl = "https://greenrebates.com/faq"
    ))
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun FaqPage() {
    Main(
        attrs = Modifier
            .classNames("d-flex", "flex-column")
            .fillMaxSize()
            .role("main")
            .toAttrs()
    ) {

        // HEADER
        Div(
            attrs = Modifier
                .wideCappedWidth()
                .classNames("mx-auto", "mt-5", "px-3")
                .toAttrs()
        ) {
            H1(
                Modifier.classNames("fs-1", "fw-bold", "text-center", "mb-2").toAttrs()
            ) {
                Text("Frequently Asked Questions")
            }
            P(
                Modifier.classNames("fs-5", "text-center", "mb-4", "mx-auto", "w-75")
                    .color(Theme.DarkGreen.rgb)
                    .opacity(0.8)
                    .toAttrs()
            ) {
                Text("Everything cannabis cultivators, indoor growers, and commercial facilities need to know about energy rebates — from first-time questions to technical details.")
            }
            HorizontalDivider(modifier = Modifier.classNames("my-4"))
        }

        // FAQ SECTIONS
        Div(
            attrs = Modifier
                .wideCappedWidth()
                .classNames("mx-auto", "px-3", "mb-5")
                .toAttrs()
        ) {

            // ── SECTION 1: REBATE QUESTIONS ──────────────────────────────────
            FaqSection(
                heading = "Rebate Questions",
                subheading = "New to rebates? Start here. These answers are written for someone who has never applied for an energy rebate before.",
                accordionId = "faq-rebate-questions",
                items = listOf(
                    FaqItem(
                        question = "What is an energy rebate?",
                        answer = "An energy rebate is a cash payment from your utility company that rewards you for upgrading to more energy-efficient equipment. When a cannabis cultivator or indoor grow operation installs qualifying LED grow lights, HVAC systems, or dehumidifiers, the utility pays back a portion of the equipment cost — sometimes up to 100%. You're essentially getting paid to run a more efficient facility."
                    ),
                    FaqItem(
                        question = "Why do utility companies offer rebates?",
                        answer = "Utilities are required or incentivized by state regulations to reduce overall energy consumption across their service territory. Instead of building new power plants, they fund rebate programs to help commercial operations — including cannabis cultivation facilities — upgrade to efficient equipment. It's cheaper for them than generating more power, and it puts real cash back in your pocket."
                    ),
                    FaqItem(
                        question = "Do cannabis and marijuana grow operations qualify for rebates?",
                        answer = "Yes. In most states where cannabis cultivation is legally licensed, cannabis and marijuana grow operations qualify for utility energy rebate programs the same way any other commercial horticulture operation does. LED grow lights, HVAC systems, and dehumidification upgrades used in licensed cultivation facilities are all commonly eligible. Green Rebates specializes in helping cannabis facilities maximize these programs."
                    ),
                    FaqItem(
                        question = "Is there a cap on how much money I can get back?",
                        answer = "Program limits vary by utility and state. Many programs have per-fixture caps or annual project maximums — but large-scale cannabis cultivation facilities have secured anywhere from $10,000 to over $200,000 in a single project. Green Rebates identifies every available program and stacks multiple rebates where the programs allow it, so you capture the maximum possible funding."
                    ),
                    FaqItem(
                        question = "What equipment qualifies for energy rebates?",
                        answer = "The most common qualifying equipment for cultivation facilities includes DLC-qualified LED grow lights, high-efficiency HVAC systems (rooftop units, mini-splits, VRF systems), and commercial dehumidifiers. Some utilities also offer rebates for controls, sensors, and other efficiency upgrades. Green Rebates identifies every applicable program for your specific equipment and utility."
                    )
                )
            )

            // ── SECTION 2: THE REBATE PROCESS ────────────────────────────────
            FaqSection(
                heading = "The Rebate Process",
                subheading = "How the application process works — and what Green Rebates handles for you.",
                accordionId = "faq-rebate-process",
                items = listOf(
                    FaqItem(
                        question = "What does Green Rebates do?",
                        answer = "Green Rebates handles the entire rebate process for your cultivation facility — from identifying available programs and submitting pre-approval applications to coordinating with your utility and following up until your check arrives. You share the basics about your operation; we handle everything else."
                    ),
                    FaqItem(
                        question = "What does it cost to work with Green Rebates?",
                        answer = "Green Rebates operates on a success-based model. We only get paid when you do — as a percentage of the rebate amount we secure on your behalf. There are no upfront fees, no contracts to sign before you know what you qualify for, and no cost to get started."
                    ),
                    FaqItem(
                        question = "Do I need pre-approval before purchasing equipment?",
                        answer = "Yes, in most cases. The majority of utility rebate programs require pre-approval before equipment is purchased or installed. Buying equipment first can disqualify your entire project — even if the equipment itself is eligible. Green Rebates manages pre-approval submissions and timing so your cultivation facility never misses a rebate due to a procedural error."
                    ),
                    FaqItem(
                        question = "How long does the rebate process take?",
                        answer = "Timelines vary by utility and program, but most rebates are approved and disbursed within 90–120 days of project completion. Some programs move faster; others require additional utility review. Green Rebates stays on top of every application and follows up proactively so delays don't cost you time or money."
                    ),
                    FaqItem(
                        question = "What information do I need to get started?",
                        answer = "Basic details about your facility: your utility provider, location, and the equipment you're planning to upgrade or have recently installed. We take it from there, assess your eligibility at no cost, and tell you exactly what programs are available before you commit to anything."
                    )
                )
            )

            // ── SECTION 3: LED GROW LIGHTS ────────────────────────────────────
            FaqSection(
                heading = "LED Grow Lights",
                subheading = "Questions about LED horticulture lighting rebates for cannabis and indoor cultivation.",
                accordionId = "faq-led-lights",
                items = listOf(
                    FaqItem(
                        question = "What types of LED grow lights qualify for rebates?",
                        answer = "Most utility programs require LED fixtures to be DLC-qualified — meaning they appear on the DesignLights Consortium Qualified Products List (QPL). This includes full-spectrum LED grow lights, LED bar systems, and LED fixtures listed under the DLC Horticultural tier for cannabis and horticulture applications. Green Rebates verifies your equipment qualifies before we start any application."
                    ),
                    FaqItem(
                        question = "How much can I get back on LED grow light upgrades?",
                        answer = "Rebate amounts depend on the number of fixtures, the wattage reduction achieved, and your utility program. Cannabis and cultivation facilities have received anywhere from a few thousand dollars to over $100,000 per LED project through Green Rebates. Larger operations replacing high-wattage HPS fixtures with efficient LED grow lights typically see the highest per-project payouts."
                    ),
                    FaqItem(
                        question = "Do I need to replace all of my grow lights to qualify?",
                        answer = "Not necessarily. Most programs allow partial upgrades — you don't have to replace every fixture in a single project. Green Rebates identifies the optimal upgrade scope to maximize your rebate while fitting your facility's timeline and budget."
                    ),
                    FaqItem(
                        question = "Can I stack LED rebates with HVAC or dehumidifier rebates?",
                        answer = "Yes. Many utility programs allow you to combine multiple rebate applications within the same project cycle. Green Rebates specifically looks for stacking opportunities — bundling LED, HVAC, and dehumidifier rebates together — to maximize the total payout for your cannabis or cultivation facility upgrade."
                    )
                )
            )

            // ── SECTION 4: DLC QUALIFIED LIGHTS ──────────────────────────────
            FaqSection(
                heading = "DLC Qualified Lights",
                subheading = "Understanding DLC certification and why it matters for your grow light rebates.",
                accordionId = "faq-dlc-lights",
                items = listOf(
                    FaqItem(
                        question = "What does DLC qualified mean?",
                        answer = "DLC stands for DesignLights Consortium. A DLC-qualified product has been independently tested and listed on the DLC Qualified Products List (QPL). Most utility rebate programs for LED grow lighting require fixtures to be DLC-qualified to process a rebate application. It's the single most important factor in determining whether your lights are eligible for a rebate."
                    ),
                    FaqItem(
                        question = "What is the DLC Horticultural tier?",
                        answer = "The DLC created a dedicated Horticultural tier for lighting used in cannabis, hemp, greenhouse, and indoor horticulture applications. Fixtures in this tier meet specialized output and spectrum requirements for plant growth and are recognized by horticulture-specific utility rebate programs. For cannabis cultivators, Horticultural-listed fixtures typically unlock the highest rebate amounts."
                    ),
                    FaqItem(
                        question = "Where can I find the DLC Qualified Products List?",
                        answer = "The DLC QPL is publicly available at designlights.org. However, the list is updated frequently and includes tiers and sub-categories that affect rebate eligibility in different ways depending on the program. Green Rebates cross-references your specific equipment against the current QPL and the requirements of each applicable utility program — so you don't have to navigate it yourself."
                    ),
                    FaqItem(
                        question = "What if my planned grow lights aren't DLC qualified?",
                        answer = "Switching to a comparable DLC-qualified fixture before purchasing is usually the simplest path to preserving full rebate eligibility. Green Rebates can identify DLC Horticultural alternatives that match your cultivation needs. In some cases we can find applicable programs for non-DLC equipment, but DLC-qualified fixtures unlock significantly more funding and should always be the default choice for cannabis grow operations."
                    )
                )
            )

            // ── SECTION 5: HVAC REBATES ───────────────────────────────────────
            FaqSection(
                heading = "HVAC Rebates",
                subheading = "Commercial HVAC energy incentives for cultivation facilities and indoor grow operations.",
                accordionId = "faq-hvac",
                items = listOf(
                    FaqItem(
                        question = "Do HVAC systems in cannabis grow operations qualify for rebates?",
                        answer = "Yes. Commercial HVAC systems — including rooftop units, mini-splits, VRF systems, and process cooling equipment — commonly qualify for utility rebate programs in cannabis and cultivation facilities. HVAC is often the second-largest rebate opportunity after LED grow lighting, and large-scale cannabis operations with significant cooling loads can see substantial payouts."
                    ),
                    FaqItem(
                        question = "What types of HVAC equipment qualify for cultivation rebates?",
                        answer = "Qualifying equipment typically includes high-efficiency rooftop units, variable refrigerant flow (VRF) systems, chillers, mini-splits, and dedicated process cooling equipment used in indoor grow operations. Specific efficiency thresholds and equipment types vary by utility and program. Green Rebates verifies your equipment against the applicable program requirements before submitting any application."
                    ),
                    FaqItem(
                        question = "Can I get HVAC and LED rebates at the same time?",
                        answer = "Yes. Many utility programs allow bundled applications covering LED grow lighting, HVAC, and dehumidification within a single project. Green Rebates handles all applications simultaneously and actively looks for stacking opportunities to maximize the total payout for your cannabis or cultivation facility upgrade."
                    )
                )
            )

            // ── SECTION 6: DEHUMIDIFIER REBATES ──────────────────────────────
            FaqSection(
                heading = "Dehumidifier Rebates",
                subheading = "Energy incentives for commercial dehumidification systems used in cultivation facilities.",
                accordionId = "faq-dehumidifier",
                items = listOf(
                    FaqItem(
                        question = "Do dehumidifiers in grow operations qualify for rebates?",
                        answer = "Yes. Commercial dehumidification systems used in cannabis and indoor cultivation facilities often qualify for utility energy rebate programs. Dehumidification is one of the highest energy loads in any cannabis cultivation facility, which makes it a strong candidate for utility incentives. The rebate amount depends on the unit's energy efficiency rating and the applicable utility program."
                    ),
                    FaqItem(
                        question = "What size dehumidifier qualifies for a rebate?",
                        answer = "Most programs target commercial-grade dehumidification equipment with specific energy efficiency thresholds. Capacity and wattage requirements vary by utility. Green Rebates reviews your system specifications and confirms eligibility before starting the application — so you know what you're working with before any equipment is purchased or installed."
                    ),
                    FaqItem(
                        question = "Can I combine dehumidifier rebates with LED or HVAC rebates?",
                        answer = "Yes. Stacking dehumidifier rebates alongside LED grow light and HVAC incentives is one of the most effective strategies for maximizing the total payout on a cultivation facility upgrade. Green Rebates specifically looks for these bundling opportunities and manages all three applications as a coordinated package wherever the programs allow it."
                    )
                )
            )

            // ── SECTION 7: CANNABIS & CULTIVATION ────────────────────────────
            FaqSection(
                heading = "Cannabis & Cultivation",
                subheading = "Rebate eligibility questions specific to cannabis cultivators, marijuana growers, and licensed horticulture operations.",
                accordionId = "faq-cannabis",
                items = listOf(
                    FaqItem(
                        question = "Are licensed cannabis and marijuana grow operations eligible for utility rebates?",
                        answer = "Yes. Licensed cannabis and marijuana cultivators qualify for utility rebate programs in most states where cultivation is legally permitted. These programs treat cannabis facilities like any other commercial horticulture or indoor grow operation — LED grow lights, HVAC, and dehumidification upgrades used in licensed cultivation facilities are all commonly eligible."
                    ),
                    FaqItem(
                        question = "Does the federal status of cannabis affect rebate eligibility?",
                        answer = "Generally, no. Utility rebate programs are administered at the state and local utility level — not the federal level. In states where cannabis cultivation is legally licensed, cultivators qualify for rebates regardless of federal scheduling. Green Rebates has helped licensed cannabis and marijuana operations across multiple states successfully navigate utility rebate programs."
                    ),
                    FaqItem(
                        question = "I run a cannabis facility and don't know where to start — what do I do?",
                        answer = "Contact Green Rebates. We'll assess your utility, equipment, and facility to identify every available LED grow light, HVAC, and dehumidifier rebate for your cannabis operation. There are no upfront costs, no account required, and we only get paid if we secure a rebate for you. It's the simplest way to find out what your cultivation facility qualifies for."
                    ),
                    FaqItem(
                        question = "Can greenhouse and mixed-light cannabis operations qualify for rebates?",
                        answer = "Yes. Greenhouse operations that use supplemental LED grow lighting alongside natural light may qualify for horticulture-specific rebate programs in most states. Mixed-light and controlled environment agriculture (CEA) facilities are increasingly recognized by utility programs, particularly those covering DLC Horticultural-listed fixtures. Green Rebates evaluates your specific operation and identifies which programs apply."
                    )
                )
            )

        }

        // FINAL CTA — full bleed
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .id("contact")
                .backgroundColor(Theme.MediumGreen.rgb)
                .toAttrs()
        ) {
            Div(attrs = Modifier.classNames("container", "text-center").toAttrs()) {
                H2(attrs = LandingCtaHeadingStyle.toModifier().classNames("mb-3").toAttrs()) {
                    Text("Ready to Find Out What Your Facility Qualifies For?")
                }
                P(attrs = LandingCtaBodyStyle.toModifier().classNames("mb-4", "mx-auto").toAttrs()) {
                    Text("Green Rebates handles every application — LED grow lights, HVAC, dehumidifiers, and more — for cannabis cultivators and commercial growers nationwide. No account, no upfront cost, no runaround.")
                }
                Link(
                    path = "/contact-us",
                    text = "Get a Free Rebate Estimate →",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaThirdStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Get a free energy rebate estimate for your cannabis or cultivation facility") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }
    }
}

@Composable
private fun FaqSection(
    heading: String,
    subheading: String,
    accordionId: String,
    items: List<FaqItem>
) {
    H2(
        attrs = Modifier
            .classNames("fw-bold", "mt-5", "mb-1")
            .color(Theme.DarkGreen.rgb)
            .fontSize(1.6.cssRem)
            .toAttrs()
    ) { Text(heading) }
    P(
        attrs = Modifier
            .classNames("mb-3")
            .color(Theme.DarkGreen.rgb)
            .opacity(0.65)
            .fontSize(0.95.cssRem)
            .toAttrs()
    ) { Text(subheading) }
    FaqAccordion(
        items = items,
        accordionId = accordionId,
        baseModifier = FaqLightAccordionStyle.toModifier()
    )
}
